import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { Auth } from "aws-amplify";
import AWS from "aws-sdk";
import {
  clearLegacyAppTokens,
  getAccessTokenSync,
} from "./tokenService";

const NO_SESSION_MESSAGE = /no current user|not authenticated|user is not authenticated/i;

export function isNoActiveSessionError(error) {
  if (!error) {
    return true;
  }
  const code = error.code || error.name || "";
  if (
    code === "NotAuthorizedException" ||
    code === "UserUnAuthenticatedException" ||
    code === "UserNotFoundException"
  ) {
    return true;
  }
  return NO_SESSION_MESSAGE.test(String(error.message || ""));
}

function isBenignRevokeError(error) {
  const code = error?.code || error?.name || "";
  return (
    code === "NotAuthorizedException" ||
    code === "InvalidTokenException" ||
    code === "UnauthorizedException"
  );
}

/**
 * Persists tokens the same way Auth.signIn does: CognitoUser#setSignInUserSession → cacheTokens
 * into localStorage under CognitoIdentityServiceProvider.* keys.
 */
export function establishCognitoSessionFromTokens({
  username,
  clientId,
  accessToken,
  idToken,
  refreshToken,
}) {
  const userPoolId = process.env.REACT_APP_USERPOOLID;
  if (!userPoolId || !clientId || !username) {
    throw new Error("Missing required Cognito session parameters");
  }
  if (!accessToken || !idToken || !refreshToken) {
    throw new Error("Missing Cognito tokens for session");
  }

  const userPool = new CognitoUserPool({
    UserPoolId: userPoolId,
    ClientId: clientId,
  });

  const cognitoUser = new CognitoUser({
    Username: username,
    Pool: userPool,
  });

  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: idToken }),
    AccessToken: new CognitoAccessToken({ AccessToken: accessToken }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: refreshToken }),
  });

  cognitoUser.setSignInUserSession(session);
  clearLegacyAppTokens();
  return cognitoUser;
}

/**
 * Before SSO token exchange: revoke prior access token (if any) and clear Amplify/Cognito session.
 */
export async function prepareForSsoTokenExchange(clientId) {
  const cognitoIdentityServiceProvider =
    new AWS.CognitoIdentityServiceProvider();

  try {
    const session = await Auth.currentSession();
    const accessToken = session.getAccessToken().getJwtToken();
    if (accessToken && clientId) {
      await cognitoIdentityServiceProvider
        .revokeToken({ Token: accessToken, ClientId: clientId })
        .promise();
    }
  } catch (error) {
    const existingAccess = getAccessTokenSync();
    if (existingAccess && clientId) {
      try {
        await cognitoIdentityServiceProvider
          .revokeToken({ Token: existingAccess, ClientId: clientId })
          .promise();
      } catch (revokeError) {
        if (!isBenignRevokeError(revokeError)) {
          console.error("Error revoking access token:", revokeError);
        }
      }
    } else if (!isNoActiveSessionError(error)) {
      console.error("Error preparing SSO session cleanup:", error);
    }
  }

  try {
    await Auth.signOut({ global: false });
  } catch (signOutError) {
    if (!isNoActiveSessionError(signOutError)) {
      console.error("Error clearing Amplify session:", signOutError);
    }
  }

  clearLegacyAppTokens();
}
